import React from 'react';
import { Chip, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Star as StarIcon
} from '@mui/icons-material';

const StyledAssistiveChip = styled(Chip)(({ theme, variant, status }) => ({
  height: '32px',
  borderRadius: '16px',
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  
  // Default variant
  ...(variant === 'default' && {
    backgroundColor: '#F5F5F5',
    border: '1px solid #E0E0E0',
    color: '#424242',
    '&:hover': {
      backgroundColor: '#EEEEEE',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Outlined variant
  ...(variant === 'outlined' && {
    backgroundColor: 'transparent',
    border: '1px solid #E0E0E0',
    color: '#424242',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },
  }),
  
  // Filled variant
  ...(variant === 'filled' && {
    backgroundColor: '#E3F2FD',
    border: 'none',
    color: '#1976D2',
    '&:hover': {
      backgroundColor: '#BBDEFB',
    },
  }),
  
  // Avatar variant
  ...(variant === 'avatar' && {
    backgroundColor: '#F3E5F5',
    border: 'none',
    color: '#7B1FA2',
    paddingLeft: '4px',
    '&:hover': {
      backgroundColor: '#E1BEE7',
    },
  }),
  
  // Status-based styling
  ...(status === 'success' && {
    backgroundColor: '#E8F5E8',
    border: '1px solid #4CAF50',
    color: '#2E7D32',
    '& .MuiChip-icon': {
      color: '#4CAF50',
    },
    '&:hover': {
      backgroundColor: '#C8E6C9',
    },
  }),
  
  ...(status === 'warning' && {
    backgroundColor: '#FFF3E0',
    border: '1px solid #FF9800',
    color: '#F57C00',
    '& .MuiChip-icon': {
      color: '#FF9800',
    },
    '&:hover': {
      backgroundColor: '#FFE0B2',
    },
  }),
  
  ...(status === 'error' && {
    backgroundColor: '#FFEBEE',
    border: '1px solid #F44336',
    color: '#D32F2F',
    '& .MuiChip-icon': {
      color: '#F44336',
    },
    '&:hover': {
      backgroundColor: '#FFCDD2',
    },
  }),
  
  ...(status === 'info' && {
    backgroundColor: '#E3F2FD',
    border: '1px solid #2196F3',
    color: '#1976D2',
    '& .MuiChip-icon': {
      color: '#2196F3',
    },
    '&:hover': {
      backgroundColor: '#BBDEFB',
    },
  }),
  
  // Icon styling
  '& .MuiChip-icon': {
    fontSize: '16px',
    marginLeft: '8px',
    marginRight: '-4px',
  },
  
  // Avatar styling
  '& .MuiChip-avatar': {
    width: '24px',
    height: '24px',
    fontSize: '12px',
    marginLeft: '4px',
    marginRight: '-4px',
  },
  
  // Delete icon styling
  '& .MuiChip-deleteIcon': {
    fontSize: '18px',
    marginLeft: '4px',
    marginRight: '-6px',
    color: 'inherit',
    '&:hover': {
      color: 'inherit',
      opacity: 0.8,
    },
  },
  
  // Label styling
  '& .MuiChip-label': {
    paddingLeft: '12px',
    paddingRight: '12px',
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const AssistiveClip = ({ 
  label = "Label",
  variant = "default", // default, outlined, filled, avatar
  status, // success, warning, error, info
  icon,
  avatar,
  avatarSrc,
  avatarText,
  onDelete,
  onClick,
  style,
  ...props 
}) => {
  // Status icons mapping
  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return icon;
    }
  };

  // Avatar component
  const getAvatar = () => {
    if (avatar) return avatar;
    if (avatarSrc) return <Avatar src={avatarSrc} />;
    if (avatarText) return <Avatar>{avatarText}</Avatar>;
    if (variant === 'avatar' && !avatar && !avatarSrc && !avatarText) {
      return <Avatar><PersonIcon /></Avatar>;
    }
    return undefined;
  };

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(event);
    }
  };

  return (
    <StyledAssistiveChip
      label={label}
      variant={variant}
      status={status}
      icon={getStatusIcon()}
      avatar={getAvatar()}
      onDelete={onDelete ? handleDelete : undefined}
      onClick={handleClick}
      style={style}
      clickable={!!onClick}
      {...props}
    />
  );
};

export default AssistiveClip;
